import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { GET_BOOKING, IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS } from 'store/queries';
import { Spinner } from 'admin-components/components';
import { Column, Page } from 'components/shared/Layout';
import { CancelButton } from 'components/ui/Button';
import { FormattedMessage } from 'react-intl';
import { historyShape, userShape } from '../../../constants/propTypes';
import { getBookingJourneysURL } from '../../../utils/urls';
import { DetailsContainer } from './CaptureTravelDocuments.styles';
import CaptureDetail from './CaptureDetail';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { StyledHeading } from './CaptureDetailForm.styles';
import { SET_VONAGE_CALL_RECORDING_STATUS } from '../../../store/mutations';
import { getPassengersWithInfantsForTravelDocuments } from '../../../utils/getInfantPassengersFromDetails';
import MissingInfantUuidModal from '../MissingInfantUuidModal';
import AlertBox from 'components/ui/AlertBox';

const CaptureTravelDocuments = ({ user, history }) => {
  const { reference } = useParams();
  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');

  const [isEligibleToTravelPerDocs, { loading: isEligibleToTravelLoading, data: isEligibleToTravelData }] = useLazyQuery(
    IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS,
    {
      fetchPolicy: 'network-only'
    }
  );

  const { data: bookingData, loading: bookingLoading } = useQuery(GET_BOOKING, {
    variables: { reference },
    fetchPolicy: 'no-cache'
  });

  const booking = bookingData?.booking;

  const [setVonageCallRecordingStatus, { loading: setVonageCallRecordingStatusLoading }] = useMutation(SET_VONAGE_CALL_RECORDING_STATUS);

  useEffect(() => {
    const pauseCallRecording = async () => {
      try {
        await setVonageCallRecordingStatus({
          variables: {
            agentEmail: user.email,
            status: 'Paused'
          }
        });
      } catch (err) {
        console.log(err);
      }
    };

    pauseCallRecording();
  }, [setVonageCallRecordingStatus, user.email]);

  useEffect(() => {
    if (booking) {
      isEligibleToTravelPerDocs({
        variables: { reference, lastName: booking?.passengers[0]?.lastName }
      });
    }
  }, [reference, booking, isEligibleToTravelPerDocs]);

  const handleTravelDocsRefresh = async () => {
    await isEligibleToTravelPerDocs({
      variables: { reference, lastName: booking?.passengers[0]?.lastName }
    });
  };

  const isPassengerRequired = (id) => {
    const products = booking.outbound
      .concat(booking.inbound)
      .map((l) => l.products)
      .flat();

    return products.some((prod) => prod.passenger.id === id && prod.passenger.travelDocumentsRequired.some((tdr) => tdr.required));
  };

  const isTravelDocumentRequired = (passenger) =>
    passenger.isInfant ? passenger.isTravelDocumentRequired : isPassengerRequired(passenger.id);

  const hasTravelDocumentBeenProvided = (passenger) =>
    passenger.isInfant
      ? passenger.hasTravelDocumentBeenProvided
      : isEligibleToTravelData?.isEligibleToTravelPerTravelDocs.find((doc) => doc.passengerUUID === passenger.uuid)?.readyToTravel;

  const resumeCallRecording = async () => {
    try {
      await setVonageCallRecordingStatus({
        variables: {
          agentEmail: user.email,
          status: 'Started'
        }
      });
    } catch (err) {
      console.log(err);
    }
  };

  const loading = isEligibleToTravelLoading || setVonageCallRecordingStatusLoading || bookingLoading;

  if (loading) {
    return (
      <Column alignItems="center" justifyContent="center">
        <Spinner />
      </Column>
    );
  }

  return (
    <Page>
      <MissingInfantUuidModal booking={booking} />
      <DetailsContainer>
        <StyledHeading>
          <FormattedMessage id="advancedPaxInfo.title" />
        </StyledHeading>

        <AlertBox variant={AlertBox.variants.WARNING}>
          <FormattedMessage id="advancedPaxInfo.pleaseComplete" />
        </AlertBox>

        {getPassengersWithInfantsForTravelDocuments({
          booking,
          isEligibleToTravelPerTravelDocs: isEligibleToTravelData?.isEligibleToTravelPerTravelDocs
        }).map((passenger) => (
          <CaptureDetail
            key={passenger.uuid}
            booking={booking}
            passenger={passenger}
            isTravelDocumentRequired={isTravelDocumentRequired(passenger)}
            hasTravelDocumentBeenProvided={hasTravelDocumentBeenProvided(passenger)}
            refreshTravelDocs={handleTravelDocsRefresh}
          />
        ))}

        <CancelButton
          onClick={() => {
            history.push(getBookingJourneysURL(reference, membershipNumber));
            resumeCallRecording();
          }}
        >
          <FormattedMessage id="advancedPaxInfo.back" />
        </CancelButton>
      </DetailsContainer>
    </Page>
  );
};

CaptureTravelDocuments.propTypes = {
  user: userShape,
  history: historyShape
};
export default CaptureTravelDocuments;
